import {saveAs} from "file-saver";

import "../css/screenshot.css";
import {PlotImageFormat, plotFileName, plotImageBlob} from "./plotImage";

/**
 * Builds a modebar icon from tabler path data. Plotly parses the markup as XML and forces it to
 * 1em, so the root needs its namespace. These are stroked letters, so fill has to be turned off
 * explicitly or they render as solid blobs.
 */
function tablerIcon(paths: string[]) {
  const children = paths.map(d => `<path style="fill:none" d="${d}"/>`).join("");

  return {
    svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="plot-export-icon"` +
      ` stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${children}</svg>`,
  };
}

/** TbSvg and TbPng, the same icons the sigma screenshot buttons use */
const SVG_ICON = tablerIcon([
  "M7 8h-3a1 1 0 0 0 -1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-3",
  "M10 8l1.5 8h1l1.5 -8",
  "M21 8h-2a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2v-4h-1",
]);

const PNG_ICON = tablerIcon([
  "M3 16v-8h2a2 2 0 1 1 0 4h-2",
  "M10 16v-8l4 8v-8",
  "M21 8h-2a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2v-4h-1",
]);

function exportButton(format: PlotImageFormat, title: string, icon: any) {
  return {
    name: `download-${format}`,
    title,
    icon,
    click: (gd: any) => {
      plotImageBlob(gd, format)
        .then((blob) => saveAs(blob, `${plotFileName(gd)}.${format}`))
        .catch((err) => console.error(err));
    },
  };
}

/**
 * Adds SVG/PNG export buttons to the modebar of every figure.
 *
 * The figures are rendered from backend built HTML that calls Plotly.newPlot with its own
 * config, and click handlers can't be serialized into that config from python. Wrapping
 * newPlot injects the buttons into every figure without each view having to wire them up.
 */
export function installPlotExportButtons() {
  const Plotly = (window as any).Plotly;

  if (!Plotly || Plotly._tcmExportButtonsInstalled) return;

  const buttons = [
    exportButton("svg", "Download as SVG", SVG_ICON),
    exportButton("png", "Download as PNG", PNG_ICON),
  ];
  const newPlot = Plotly.newPlot;

  Plotly.newPlot = function (gd: any, data: any, layout: any, config: any = {}) {
    // The offscreen render behind the SVG export comes back through here, and has no modebar
    if (config.staticPlot) return newPlot.call(this, gd, data, layout, config);

    // Previews come with the modebar switched off, give them one holding only these buttons
    const isPreview = config.displayModeBar === false;

    const patched = isPreview
      ? {...config, displayModeBar: "hover", displaylogo: false, modeBarButtons: [buttons]}
      : {
        ...config,
        modeBarButtonsToAdd: [...(config.modeBarButtonsToAdd ?? []), ...buttons],
        // Drop the built in camera, it only ever produces PNG
        modeBarButtonsToRemove: [...(config.modeBarButtonsToRemove ?? []), "toImage"],
      };

    return newPlot.call(this, gd, data, layout, patched);
  };

  Plotly._tcmExportButtonsInstalled = true;
}
